/* Admin dashboard — minimal, dashboard aesthetic matching the viewer. */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { LogOut, Home, RefreshCw, Download, ShieldOff, ShieldCheck } from "lucide-react";
import "./viewer/viewer.css";

const API = process.env.REACT_APP_BACKEND_URL + "/api/admin";
const Q_API = process.env.REACT_APP_BACKEND_URL + "/api";
const TOKEN_KEY = "tbg_admin_token";
const EXP_KEY = "tbg_admin_token_exp";

const getToken = () => {
  const exp = localStorage.getItem(EXP_KEY);
  if (exp && new Date(exp) < new Date()) {
    localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(EXP_KEY);
    return null;
  }
  return localStorage.getItem(TOKEN_KEY);
};
const clearAdminToken = () => {
  localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(EXP_KEY);
};
const authedFetch = async (url) => {
  const t = getToken();
  const res = await fetch(url, { headers: t ? { Authorization: `Bearer ${t}` } : {} });
  if (res.status === 401 || res.status === 403) { clearAdminToken(); throw new Error("AUTH"); }
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};
const authedPost = async (url, body) => {
  const t = getToken();
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401 || res.status === 403) { clearAdminToken(); throw new Error("AUTH"); }
  if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
  return data;
};

export default function Admin() {
  const [authed, setAuthed] = useState(!!getToken());
  if (!authed) return <AdminLogin onAuthed={() => setAuthed(true)} />;
  return <AdminDashboard onLogout={() => { clearAdminToken(); setAuthed(false); }} />;
}

function AdminLogin({ onAuthed }) {
  const [email, setEmail] = useState("admin@tbirdgeo.com");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const res = await fetch(`${API}/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password: pw }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(EXP_KEY, data.expires_at);
      onAuthed();
    } catch (ex) {
      setErr(formatErr(ex.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="tbg-vw" data-testid="admin-login-page">
      <div className="tbg-vw-modal">
        <form className="tbg-vw-modal-card" onSubmit={submit}>
          <div className="h-sub">TBG · Admin</div>
          <h2>Sign in</h2>
          <div style={{ height: 18 }} />
          <input
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Admin email" className="tbg-vw-input"
            data-testid="admin-email" style={{ marginBottom: 10 }}
          />
          <input
            type="password" required value={pw} onChange={(e) => setPw(e.target.value)}
            placeholder="Password" autoFocus className="tbg-vw-input"
            data-testid="admin-password"
          />
          {err && <div className="tbg-vw-modal-err" data-testid="admin-error">{err}</div>}
          <div className="tbg-vw-modal-foot">
            <Link to="/" className="tbg-vw-btn ghost">Cancel</Link>
            <button type="submit" disabled={busy || !pw} className="tbg-vw-btn primary" data-testid="admin-submit">
              {busy ? "Verifying…" : "Sign in"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function formatErr(msg) {
  if (!msg) return "Sign-in failed.";
  if (msg.includes("Too many")) return msg;
  if (msg === "AUTH" || msg === "HTTP 401") return "Invalid credentials.";
  return msg;
}

function AdminDashboard({ onLogout }) {
  const [tab, setTab] = useState("quotes");
  const [quotes, setQuotes] = useState([]);
  const [studios, setStudios] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [audit, setAudit] = useState([]);
  const [certs, setCerts] = useState([]);
  const [emailHealth, setEmailHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const refresh = async () => {
    setLoading(true);
    try {
      const [q, s, w, a, c, eh] = await Promise.all([
        authedFetch(`${Q_API}/quotes`),
        authedFetch(`${Q_API}/studios/inquiries`),
        authedFetch(`${Q_API}/groundschool/waitlist`),
        authedFetch(`${API}/audit`),
        authedFetch(`${Q_API}/certificates`),
        authedFetch(`${Q_API}/payments/admin/email-health?days=7`),
      ]);
      setQuotes(q); setStudios(s); setWaitlist(w); setAudit(a); setCerts(c); setEmailHealth(eh);
    } catch (e) {
      if (e.message === "AUTH") { onLogout(); return; }
      toast.error("Load failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const onRevoke = async (number) => {
    const reason = window.prompt(
      `Revoke certificate ${number}?\n\nEnter a reason (visible on the certificate PDF as a REVOKED watermark and in the /verify response):`
    );
    if (!reason || reason.trim().length < 3) return;
    try {
      await authedPost(`${Q_API}/certificates/${encodeURIComponent(number)}/revoke`, { reason: reason.trim() });
      toast.success(`Revoked ${number}`);
      refresh();
    } catch (e) {
      if (e.message === "AUTH") { onLogout(); return; }
      toast.error(e.message || "Revoke failed");
    }
  };

  const onReinstate = async (number) => {
    if (!window.confirm(`Reinstate certificate ${number}? This will remove the revoked flag and the PDF will render without the REVOKED watermark.`)) return;
    try {
      await authedPost(`${Q_API}/certificates/${encodeURIComponent(number)}/reinstate`);
      toast.success(`Reinstated ${number}`);
      refresh();
    } catch (e) {
      if (e.message === "AUTH") { onLogout(); return; }
      toast.error(e.message || "Reinstate failed");
    }
  };

  const onDownload = async (number) => {
    try {
      const t = getToken();
      const r = await fetch(`${Q_API}/certificates/${encodeURIComponent(number)}/pdf`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `TGI-Certificate-${number}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      toast.error(e.message || "Download failed");
    }
  };

  return (
    <div className="tbg-vw" data-testid="admin-page">
      <Toaster theme="dark" position="top-right" />
      <div className="tbg-vw-top">
        <Link to="/" className="tbg-vw-brand">
          <img src="https://customer-assets.emergentagent.com/job_tbirdgeo-preview/artifacts/3gk1w13g_FullLogo_Transparent_NoBuffer%20%2812%29.png" alt="TBG" />
          <span className="sub">Admin</span>
        </Link>
        <div className="tbg-vw-mode">
          <button className={tab === "quotes" ? "active" : ""} onClick={() => setTab("quotes")} data-testid="admin-tab-quotes">Quotes</button>
          <button className={tab === "studios" ? "active" : ""} onClick={() => setTab("studios")} data-testid="admin-tab-studios">Studios</button>
          <button className={tab === "students" ? "active" : ""} onClick={() => setTab("students")} data-testid="admin-tab-students">Students</button>
          <button className={tab === "waitlist" ? "active" : ""} onClick={() => setTab("waitlist")} data-testid="admin-tab-waitlist">Mailing List</button>
          <button className={tab === "certs" ? "active" : ""} onClick={() => setTab("certs")} data-testid="admin-tab-certs">Certificates</button>
          <button className={tab === "analytics" ? "active" : ""} onClick={() => setTab("analytics")} data-testid="admin-tab-analytics">Analytics</button>
          <button className={tab === "email" ? "active" : ""} onClick={() => setTab("email")} data-testid="admin-tab-email-health">Email Health</button>
          <button className={tab === "audit" ? "active" : ""} onClick={() => setTab("audit")} data-testid="admin-tab-audit">Audit</button>
        </div>
        <div className="tbg-vw-actions">
          <button className="tbg-vw-btn ghost" onClick={refresh} title="Refresh">
            <RefreshCw size={12} className={loading ? "spin" : ""} /> Refresh
          </button>
          <Link to="/admin/authoring" className="tbg-vw-btn ghost" data-testid="admin-authoring-link">
            Content
          </Link>
          <Link to="/" className="tbg-vw-btn ghost"><Home size={12} /> Site</Link>
          <button className="tbg-vw-btn ghost" onClick={() => { onLogout(); nav("/"); }} data-testid="admin-logout">
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </div>

      <div style={{ overflow: "auto", padding: 16, background: "#050807" }}>
        {tab === "quotes" && <Table rows={quotes} cols={QUOTE_COLS} testid="admin-table-quotes" empty="No quotes yet." />}
        {tab === "studios" && <Table rows={studios} cols={STUDIO_COLS} testid="admin-table-studios" empty="No studios inquiries yet." />}
        {tab === "students" && <StudentsPanel onLogout={onLogout} />}
        {tab === "waitlist" && <Table rows={waitlist} cols={WAITLIST_COLS} testid="admin-table-waitlist" empty="No mailing list signups yet." />}
        {tab === "certs" && (
          <CertsTable rows={certs} onDownload={onDownload} onRevoke={onRevoke} onReinstate={onReinstate} />
        )}
        {tab === "analytics" && <PaymentAnalyticsPanel onLogout={onLogout} />}
        {tab === "email" && <EmailHealthPanel data={emailHealth} />}
        {tab === "audit" && <Table rows={audit} cols={AUDIT_COLS} testid="admin-table-audit" empty="No audit events yet." />}
      </div>
    </div>
  );
}

const QUOTE_COLS = [
  ["created_at", "When", fmtDate],
  ["name", "Name"],
  ["email", "Email"],
  ["phone", "Phone"],
  ["company", "Company"],
  ["service", "Service"],
  ["location", "Location"],
  ["project_details", "Details", (v) => clip(v, 80)],
  ["email_sent", "Email", (v) => (v ? "✓" : "—")],
];
const STUDIO_COLS = [
  ["created_at", "When", fmtDate],
  ["name", "Contact"],
  ["email", "Email"],
  ["phone", "Phone"],
  ["production_name", "Production"],
  ["production_type", "Type"],
  ["shoot_window", "Window"],
  ["location", "Location"],
  ["capabilities", "Caps", (v) => (Array.isArray(v) ? v.join(", ") : "")],
  ["project_details", "Details", (v) => clip(v, 80)],
];
const AUDIT_COLS = [
  ["timestamp", "When", fmtDate],
  ["admin_email", "Admin"],
  ["method", "M"],
  ["path", "Path"],
  ["status", "Status"],
  ["reason", "Reason"],
  ["ip", "IP"],
];
const WAITLIST_COLS = [
  ["created_at", "When", fmtDate],
  ["name", "Name"],
  ["email", "Email"],
  ["phone", "Phone"],
  ["experience", "Experience"],
  ["source", "Source"],
];

function Table({ rows, cols, testid, empty }) {
  if (!rows || rows.length === 0) {
    return <div style={{ color: "#7b8b87", padding: 24, fontFamily: "var(--vw-mono)", fontSize: 12 }}>{empty}</div>;
  }
  return (
    <div data-testid={testid} style={{ overflowX: "auto", border: "1px solid #1c2a27", borderRadius: 4 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "var(--vw-mono)", fontSize: 11, color: "#e2ece9" }}>
        <thead>
          <tr>
            {cols.map(([k, label]) => (
              <th key={k} style={{ background: "#0d1817", color: "#7b8b87", textAlign: "left", padding: "9px 12px", borderBottom: "1px solid #1c2a27", letterSpacing: "0.2em", textTransform: "uppercase", fontSize: 10 }}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || r.timestamp || i} style={{ borderBottom: "1px solid #11201d", background: i % 2 ? "#070f0e" : "#091514" }}>
              {cols.map(([k, , fmt]) => (
                <td key={k} style={{ padding: "8px 12px", verticalAlign: "top" }}>
                  {fmt ? fmt(r[k]) : (r[k] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function fmtDate(v) {
  if (!v) return "—";
  try { return new Date(v).toISOString().replace("T", " ").slice(0, 19); }
  catch (e) { return String(v); }
}
function clip(v, n) {
  if (!v) return "—";
  return v.length > n ? v.slice(0, n) + "…" : v;
}

/* ------------------------ Certificates ------------------------ */

function CertsTable({ rows, onDownload, onRevoke, onReinstate }) {
  if (!rows || rows.length === 0) {
    return <div style={{ color: "#7b8b87", padding: 24, fontFamily: "var(--vw-mono)", fontSize: 12 }} data-testid="admin-certs-empty">No certificates issued yet.</div>;
  }
  const active = rows.filter((r) => !r.revoked).length;
  const revoked = rows.length - active;
  return (
    <div data-testid="admin-table-certs">
      <div style={{ color: "#7b8b87", fontFamily: "var(--vw-mono)", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 10 }}>
        {rows.length} total · <span style={{ color: "#3dc0af" }}>{active} active</span>
        {revoked > 0 && <> · <span style={{ color: "#f47171" }}>{revoked} revoked</span></>}
      </div>
      <div style={{ overflowX: "auto", border: "1px solid #1c2a27", borderRadius: 4 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "var(--vw-mono)", fontSize: 11, color: "#e2ece9" }}>
          <thead>
            <tr>
              {["Issued", "Number", "Student", "Email", "Course", "Score", "Status", "Actions"].map((h) => (
                <th key={h} style={{ background: "#0d1817", color: "#7b8b87", textAlign: "left", padding: "9px 12px", borderBottom: "1px solid #1c2a27", letterSpacing: "0.2em", textTransform: "uppercase", fontSize: 10 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id || r.number} style={{ borderBottom: "1px solid #11201d", background: i % 2 ? "#070f0e" : "#091514" }} data-testid={`admin-cert-row-${r.number}`}>
                <td style={{ padding: "8px 12px", verticalAlign: "top" }}>{fmtDate(r.issued_at)}</td>
                <td style={{ padding: "8px 12px", verticalAlign: "top", color: r.revoked ? "#f47171" : "#3dc0af" }}>{r.number}</td>
                <td style={{ padding: "8px 12px", verticalAlign: "top" }}>{r.student_name || "—"}</td>
                <td style={{ padding: "8px 12px", verticalAlign: "top", color: "#a5b8b3" }}>{r.student_email || "—"}</td>
                <td style={{ padding: "8px 12px", verticalAlign: "top" }}>{clip(r.course_name, 34)}</td>
                <td style={{ padding: "8px 12px", verticalAlign: "top" }}>
                  {r.score_pct != null ? `${r.score_pct.toFixed(1)}%` : "—"}
                </td>
                <td style={{ padding: "8px 12px", verticalAlign: "top" }}>
                  {r.revoked ? (
                    <span style={{ color: "#f47171" }} title={r.revoked_reason || ""}>
                      REVOKED{r.revoked_reason ? ` · ${clip(r.revoked_reason, 40)}` : ""}
                    </span>
                  ) : (
                    <span style={{ color: "#3dc0af" }}>Active</span>
                  )}
                </td>
                <td style={{ padding: "6px 12px", verticalAlign: "top" }}>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <button
                      className="tbg-vw-btn ghost"
                      onClick={() => onDownload(r.number)}
                      title="Download PDF"
                      data-testid={`admin-cert-download-${r.number}`}
                    >
                      <Download size={11} /> PDF
                    </button>
                    {r.revoked ? (
                      <button
                        className="tbg-vw-btn ghost"
                        onClick={() => onReinstate(r.number)}
                        title="Reinstate this certificate"
                        data-testid={`admin-cert-reinstate-${r.number}`}
                        style={{ color: "#3dc0af", borderColor: "rgba(61,192,175,0.4)" }}
                      >
                        <ShieldCheck size={11} /> Reinstate
                      </button>
                    ) : (
                      <button
                        className="tbg-vw-btn ghost"
                        onClick={() => onRevoke(r.number)}
                        title="Revoke this certificate"
                        data-testid={`admin-cert-revoke-${r.number}`}
                        style={{ color: "#f47171", borderColor: "rgba(244,113,113,0.4)" }}
                      >
                        <ShieldOff size={11} /> Revoke
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* --------------------------------------------------------------------
   EmailHealthPanel — tiny "did welcome emails actually land?" tile +
   last-20-failures table. Backed by GET /api/payments/admin/email-health.
   Each failure row surfaces to/kind/status_code/error so support can
   tell apart "unverified sender" (403) from a bounce from a real
   outage without tailing pod logs.
   -------------------------------------------------------------------- */
function EmailHealthPanel({ data }) {
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  const runBackfill = async () => {
    const n = (data?.failed || 0) + (data?.skipped || 0);
    const msg = n > 0
      ? `Resend welcome email to the ${n} buyer(s) whose first send failed? This is safe to re-run.`
      : "No current failures in this window — resend anyway for any stuck tx outside the window?";
    if (!window.confirm(msg)) return;
    setBackfillBusy(true);
    setBackfillResult(null);
    try {
      const r = await authedFetch(`${Q_API}/payments/admin/resend-welcome-backfill`, { method: "POST" });
      setBackfillResult(r);
      const msg2 = `Backfill done — ${r.succeeded}/${r.attempted} succeeded, ${r.failed} failed.`;
      if (r.failed === 0 && r.succeeded > 0) toast.success(msg2);
      else if (r.attempted === 0) toast.info("No stuck buyers to rescue.");
      else toast.warning(msg2);
    } catch (e) {
      toast.error(`Backfill failed: ${e.message}`);
    } finally {
      setBackfillBusy(false);
    }
  };

  if (!data) {
    return (
      <div style={{ color: "#7b8b87", padding: 24, fontFamily: "var(--vw-mono)", fontSize: 12 }}>
        Loading email health…
      </div>
    );
  }
  const { window_days, total, sent, failed, skipped, success_rate, last_failures, by_kind, throttled } = data;
  const pct = Math.round((success_rate || 0) * 1000) / 10;
  const healthy = failed === 0 && skipped === 0;
  const headlineColor = healthy ? "#3dc0af" : (sent === 0 ? "#f47171" : "#f0be3c");

  const Stat = ({ label, value, color }) => (
    <div style={{
      flex: 1, minWidth: 140,
      background: "#091514", border: "1px solid #1c2a27", borderRadius: 4,
      padding: "14px 16px",
    }}>
      <div style={{ font: "700 10px var(--vw-mono)", letterSpacing: "0.2em",
        color: "#7b8b87", textTransform: "uppercase", marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ font: "700 22px 'Montserrat','Geist','Inter'", color: color || "#e2ece9" }}>
        {value}
      </div>
    </div>
  );

  return (
    <div data-testid="admin-email-health-panel" style={{ display: "grid", gap: 16 }}>
      {/* Headline */}
      <div style={{
        background: "#091514", border: "1px solid #1c2a27", borderRadius: 4,
        padding: "16px 18px",
        display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16,
      }}>
        <div>
          <div style={{ font: "700 10px var(--vw-mono)", letterSpacing: "0.2em",
            color: "#7b8b87", textTransform: "uppercase", marginBottom: 6 }}>
            Welcome email success rate · last {window_days} days
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
            <div data-testid="admin-email-health-rate"
              style={{ font: "700 36px 'Montserrat','Geist','Inter'", color: headlineColor, letterSpacing: "-0.02em" }}>
              {pct.toFixed(1)}%
            </div>
            <div style={{ color: "#7b8b87", font: "500 12px var(--vw-mono)" }}>
              {sent}/{sent + failed} delivered · {total} total events
            </div>
          </div>
        </div>
        <button
          data-testid="admin-email-health-backfill"
          onClick={runBackfill}
          disabled={backfillBusy}
          style={{
            background: backfillBusy ? "#1c2a27" : "#3dc0af",
            color: backfillBusy ? "#7b8b87" : "#051210",
            border: 0, padding: "10px 16px", borderRadius: 999,
            font: "700 11px var(--vw-mono)", letterSpacing: "0.1em",
            textTransform: "uppercase", cursor: backfillBusy ? "wait" : "pointer",
            whiteSpace: "nowrap",
          }}
          title="Resend welcome email to every buyer whose first send failed (safe to re-run)"
        >
          {backfillBusy ? "Rescuing…" : "Rescue stuck buyers"}
        </button>
      </div>

      {/* Backfill result */}
      {backfillResult && (
        <div data-testid="admin-email-health-backfill-result"
          style={{ background: "#091514", border: "1px solid #1c2a27",
            borderRadius: 4, padding: "14px 16px",
            font: "500 12px var(--vw-mono)", color: "#e2ece9" }}>
          <div style={{ marginBottom: 8, color: "#7b8b87",
            letterSpacing: "0.2em", textTransform: "uppercase", fontSize: 10, fontWeight: 700 }}>
            Backfill result
          </div>
          <div>attempted: {backfillResult.attempted}</div>
          <div>succeeded: <span style={{ color: "#3dc0af" }}>{backfillResult.succeeded}</span></div>
          <div>failed: <span style={{ color: backfillResult.failed > 0 ? "#f47171" : "#7b8b87" }}>
            {backfillResult.failed}</span></div>
          {backfillResult.results && backfillResult.results.length > 0 && (
            <div style={{ marginTop: 10, maxHeight: 180, overflowY: "auto",
              borderTop: "1px solid #11201d", paddingTop: 8 }}>
              {backfillResult.results.map((r, i) => (
                <div key={i} style={{ display: "flex", gap: 10, padding: "4px 0",
                  borderBottom: "1px solid #0d1817" }}>
                  <span style={{ color: r.ok ? "#3dc0af" : "#f47171", width: 60 }}>
                    {r.ok ? "✓ sent" : "✗ failed"}
                  </span>
                  <span style={{ flex: 1 }}>{r.email}</span>
                  <span style={{ color: "#7b8b87" }}>
                    {r.ok ? (r.message_id?.slice(0, 12) || "") : clip(r.error || "", 60)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <Stat label="Sent" value={sent} color="#3dc0af" />
        <Stat label="Failed" value={failed} color={failed > 0 ? "#f47171" : "#e2ece9"} />
        <Stat label="Skipped" value={skipped} color={skipped > 0 ? "#f0be3c" : "#e2ece9"} />
        <Stat label="Throttled" value={throttled || 0} color={(throttled || 0) > 0 ? "#c9a8ff" : "#e2ece9"} />
        <Stat label="Total" value={total} />
      </div>

      {/* By-kind breakdown — one tile per email kind so admin can see
          onboarding volume vs. forgot-password volume vs. admin-rescue
          volume at a glance. */}
      {by_kind && by_kind.length > 0 && (
        <div>
          <div style={{ font: "700 10px var(--vw-mono)", letterSpacing: "0.2em",
            color: "#7b8b87", textTransform: "uppercase", margin: "4px 0 10px" }}>
            By email kind
          </div>
          <div data-testid="admin-email-health-by-kind"
            style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
            {by_kind.map((bk) => <KindCard key={bk.kind} bk={bk} />)}
          </div>
        </div>
      )}

      {/* Failures */}
      <div>
        <div style={{ font: "700 10px var(--vw-mono)", letterSpacing: "0.2em",
          color: "#7b8b87", textTransform: "uppercase", margin: "4px 0 10px" }}>
          Recent failures / skips
        </div>
        {(!last_failures || last_failures.length === 0) ? (
          <div data-testid="admin-email-health-empty"
            style={{ color: "#3dc0af", padding: "14px 16px",
              background: "rgba(61,192,175,0.06)",
              border: "1px solid rgba(61,192,175,0.3)", borderRadius: 4,
              font: "500 12px var(--vw-mono)" }}>
            ✓ No failed or skipped welcome emails in the last {window_days} days.
          </div>
        ) : (
          <div data-testid="admin-email-health-failures"
            style={{ overflowX: "auto", border: "1px solid #1c2a27", borderRadius: 4 }}>
            <table style={{ width: "100%", borderCollapse: "collapse",
              fontFamily: "var(--vw-mono)", fontSize: 11, color: "#e2ece9" }}>
              <thead>
                <tr>
                  {["When", "To", "Kind", "Status", "HTTP", "Error"].map((h) => (
                    <th key={h} style={{ background: "#0d1817", color: "#7b8b87",
                      textAlign: "left", padding: "9px 12px",
                      borderBottom: "1px solid #1c2a27", letterSpacing: "0.2em",
                      textTransform: "uppercase", fontSize: 10 }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {last_failures.map((r, i) => (
                  <tr key={`${r.timestamp}-${i}`}
                    style={{ borderBottom: "1px solid #11201d",
                      background: i % 2 ? "#070f0e" : "#091514" }}>
                    <td style={{ padding: "8px 12px", verticalAlign: "top", whiteSpace: "nowrap" }}>
                      {fmtDate(r.timestamp)}
                    </td>
                    <td style={{ padding: "8px 12px", verticalAlign: "top" }}>{r.to}</td>
                    <td style={{ padding: "8px 12px", verticalAlign: "top", color: "#c9a8ff" }}>{r.kind}</td>
                    <td style={{ padding: "8px 12px", verticalAlign: "top",
                      color: r.status === "failed" ? "#f47171" : "#f0be3c" }}>
                      {r.status}
                    </td>
                    <td style={{ padding: "8px 12px", verticalAlign: "top", color: "#7b8b87" }}>
                      {r.status_code || "—"}
                    </td>
                    <td style={{ padding: "8px 12px", verticalAlign: "top", color: "#7b8b87" }}>
                      {clip(r.error || "", 90)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------
   StudentsPanel — searchable table of buyers + click-through to a
   per-student profile drawer. Above the fold on the profile view:
   login history, course progress per module, exam attempts. Payments
   and welcome-email audit live further down.
   Backed by:
     GET /api/students/admin/list?q=…
     GET /api/students/admin/{id}
   -------------------------------------------------------------------- */
function StudentsPanel({ onLogout }) {
  const [query, setQuery] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("");
  const [progressFilter, setProgressFilter] = useState("");
  const [loginTypeFilter, setLoginTypeFilter] = useState("");
  const [schools, setSchools] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.set("q", query);
      if (schoolFilter) params.set("school_licence_id", schoolFilter);
      if (progressFilter) params.set("progress", progressFilter);
      if (loginTypeFilter) params.set("login_type", loginTypeFilter);
      const url = `${Q_API}/students/admin/list${params.toString() ? `?${params}` : ""}`;
      const data = await authedFetch(url);
      setRows(data.rows || []);
    } catch (e) {
      if (e.message === "AUTH") { onLogout(); return; }
      toast.error("Failed to load students");
    } finally { setLoading(false); }
  };

  // Load the licence list once so we can offer a School filter dropdown.
  useEffect(() => {
    (async () => {
      try {
        const d = await authedFetch(`${Q_API}/admin/school-licences`);
        setSchools(d.rows || []);
      } catch (e) { /* non-fatal */ }
    })();
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [schoolFilter, progressFilter, loginTypeFilter]);

  const openProfile = async (id) => {
    try {
      const profile = await authedFetch(`${Q_API}/students/admin/${id}`);
      setSelected(profile);
    } catch (e) {
      if (e.message === "AUTH") { onLogout(); return; }
      toast.error("Failed to load profile");
    }
  };

  return (
    <div data-testid="admin-students-panel" style={{ display: "grid", gap: 14 }}>
      {/* Search bar + filters */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <input
          type="text"
          value={query}
          placeholder="Search by name or email…"
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") load(); }}
          data-testid="admin-students-search"
          style={{
            flex: 1, minWidth: 200, background: "#091514", border: "1px solid #1c2a27",
            borderRadius: 4, color: "#e2ece9", padding: "10px 14px",
            fontFamily: "var(--vw-mono)", fontSize: 12,
          }}
        />
        <select value={loginTypeFilter} onChange={(e) => setLoginTypeFilter(e.target.value)}
          data-testid="admin-students-filter-logintype" style={_filterStyle}>
          <option value="">All logins</option>
          <option value="email">Email only</option>
          <option value="school">School ID only</option>
        </select>
        <select value={schoolFilter} onChange={(e) => setSchoolFilter(e.target.value)}
          data-testid="admin-students-filter-school" style={_filterStyle}>
          <option value="">All schools</option>
          {schools.map((s) => <option key={s.id} value={s.id}>{s.organization_name}</option>)}
        </select>
        <select value={progressFilter} onChange={(e) => setProgressFilter(e.target.value)}
          data-testid="admin-students-filter-progress" style={_filterStyle}>
          <option value="">All progress</option>
          <option value="not_started">Not started</option>
          <option value="in_progress">In progress</option>
          <option value="completed">Completed</option>
        </select>
        <button
          onClick={() => load()}
          disabled={loading}
          data-testid="admin-students-search-btn"
          style={{
            background: "#3dc0af", color: "#051210", border: 0,
            padding: "10px 18px", borderRadius: 999, cursor: "pointer",
            font: "700 11px var(--vw-mono)", letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          {loading ? "Loading…" : "Search"}
        </button>
      </div>

      {/* Students table */}
      {(!rows || rows.length === 0) ? (
        <div style={{ color: "#7b8b87", padding: 24, fontFamily: "var(--vw-mono)", fontSize: 12 }}>
          {loading ? "Loading…" : "No students found."}
        </div>
      ) : (
        <div data-testid="admin-students-table"
          style={{ overflowX: "auto", border: "1px solid #1c2a27", borderRadius: 4 }}>
          <table style={{ width: "100%", borderCollapse: "collapse",
            fontFamily: "var(--vw-mono)", fontSize: 11, color: "#e2ece9" }}>
            <thead>
              <tr>
                {["Name", "Email / School", "Type", "Onboarded", "Last login", "Progress", "Lessons", "Exams", "Best", ""].map((h) => (
                  <th key={h} style={{ background: "#0d1817", color: "#7b8b87",
                    textAlign: "left", padding: "9px 12px",
                    borderBottom: "1px solid #1c2a27", letterSpacing: "0.2em",
                    textTransform: "uppercase", fontSize: 10 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id}
                  onClick={() => openProfile(r.id)}
                  data-testid={`admin-students-row-${r.email || r.id}`}
                  style={{ borderBottom: "1px solid #11201d",
                    background: i % 2 ? "#070f0e" : "#091514",
                    cursor: "pointer" }}>
                  <td style={{ padding: "8px 12px", color: "#e2ece9" }}>{r.name || "—"}</td>
                  <td style={{ padding: "8px 12px" }}>
                    {r.login_type === "school"
                      ? <span style={{ color: "#c9a8ff" }}>{r.school_name || "—"}</span>
                      : (r.email || "—")}
                  </td>
                  <td style={{ padding: "8px 12px" }}>
                    <span style={{
                      display: "inline-block", padding: "2px 8px", borderRadius: 999,
                      font: "700 9px var(--vw-mono)", letterSpacing: "0.15em",
                      textTransform: "uppercase",
                      background: r.login_type === "school" ? "rgba(201,168,255,0.12)" : "rgba(61,192,175,0.12)",
                      color: r.login_type === "school" ? "#c9a8ff" : "#3dc0af",
                      border: "1px solid " + (r.login_type === "school" ? "rgba(201,168,255,0.4)" : "rgba(61,192,175,0.4)"),
                    }}>
                      {r.login_type === "school" ? "School" : "Email"}
                    </span>
                  </td>
                  <td style={{ padding: "8px 12px",
                    color: r.onboarded ? "#3dc0af" : "#f0be3c" }}>
                    {r.onboarded ? `✓ ${fmtDate(r.first_login_at)}` : "✗ pending"}
                  </td>
                  <td style={{ padding: "8px 12px", color: "#7b8b87" }}>
                    {r.last_login_at ? fmtDate(r.last_login_at) : "—"}
                  </td>
                  <td style={{ padding: "8px 12px",
                    color: r.progress_status === "completed" ? "#3dc0af"
                      : (r.progress_status === "in_progress" ? "#f0be3c" : "#7b8b87") }}>
                    {r.progress_status === "completed" ? "Completed"
                      : r.progress_status === "in_progress" ? "In progress"
                      : "Not started"}
                  </td>
                  <td style={{ padding: "8px 12px" }}>{r.completed_lesson_count}</td>
                  <td style={{ padding: "8px 12px" }}>{r.exam_attempts_count}</td>
                  <td style={{ padding: "8px 12px",
                    color: r.passed_exam ? "#3dc0af" : (r.best_exam_pct != null ? "#f0be3c" : "#7b8b87") }}>
                    {r.best_exam_pct != null
                      ? `${r.best_exam_pct}%${r.passed_exam ? " ✓" : ""}`
                      : "—"}
                  </td>
                  <td style={{ padding: "6px 12px" }} onClick={(e) => e.stopPropagation()}>
                    <EmailStudentButton
                      email={r.email}
                      name={r.name}
                      firstName={r.first_name}
                      pct={r.overall_percent}
                      testId={`admin-email-${r.email || r.id}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && <StudentProfileDrawer profile={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

/* --------------------------------------------------------------------
   StudentProfileDrawer — modal with full-detail view. Login activity
   + per-module progress + exam attempts are above-the-fold. Payments
   and welcome-email audit are expandable sections below.
   -------------------------------------------------------------------- */
function StudentProfileDrawer({ profile, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 90,
        background: "rgba(5,8,7,0.86)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "60px 20px", overflowY: "auto",
      }}
    >
      <div
        data-testid="admin-student-profile"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#050807", border: "1px solid #1c2a27",
          borderRadius: 6, maxWidth: 920, width: "100%", color: "#e2ece9",
          fontFamily: "var(--vw-mono)",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between",
          alignItems: "center", padding: "18px 22px",
          borderBottom: "1px solid #1c2a27" }}>
          <div>
            <div style={{ color: "#3dc0af", font: "700 10px var(--vw-mono)",
              letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 4 }}>
              Student profile
            </div>
            <div style={{ font: "700 20px 'Montserrat','Geist','Inter'", color: "#e2ece9" }}>
              {profile.name || profile.email}
            </div>
            <div style={{ color: "#7b8b87", fontSize: 11, marginTop: 2 }}>
              {profile.email} · created {fmtDate(profile.created_at)}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <EmailStudentButton
              email={profile.email}
              name={profile.name}
              firstName={profile.first_name}
              testId="admin-student-profile-email"
              large
            />
            <button onClick={onClose} data-testid="admin-student-profile-close"
              style={{ background: "transparent", border: "1px solid #2a403c",
                color: "#a5b8b3", padding: "6px 14px", borderRadius: 999,
                cursor: "pointer", fontSize: 11 }}>
              ✕ Close
            </button>
          </div>
        </div>

        <div style={{ padding: "20px 22px", display: "grid", gap: 20 }}>
          {/* 1. Login history */}
          <ProfileSection title="Login history">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
              <KV label="Password set" value={fmtDate(profile.password_set_at) || "—"}
                color={profile.password_set_at ? "#3dc0af" : "#f0be3c"} />
              <KV label="First login" value={fmtDate(profile.first_login_at) || "—"}
                color={profile.first_login_at ? "#3dc0af" : "#f0be3c"} />
              <KV label="Last login" value={fmtDate(profile.last_login_at) || "—"} />
              <KV label="Total logins" value={profile.login_count}
                color={profile.login_count > 0 ? "#3dc0af" : "#7b8b87"} />
            </div>
          </ProfileSection>

          {/* 2. Course progress */}
          <ProfileSection title={`Course progress (${profile.progress.length} course${profile.progress.length === 1 ? "" : "s"})`}>
            {profile.progress.length === 0 ? (
              <div style={{ color: "#7b8b87", fontSize: 11 }}>No course access yet.</div>
            ) : profile.progress.map((p) => (
              <div key={p.course_id} style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between",
                  alignItems: "baseline", marginBottom: 8 }}>
                  <div style={{ color: "#e2ece9", fontSize: 12, fontWeight: 700 }}>
                    {p.course_title}
                  </div>
                  <div style={{ color: p.overall_percent >= 80 ? "#3dc0af"
                    : (p.overall_percent > 0 ? "#f0be3c" : "#7b8b87"), fontSize: 11 }}>
                    {p.overall_percent}% overall
                  </div>
                </div>
                <div style={{ display: "grid", gap: 6 }}>
                  {p.modules.map((m) => (
                    <div key={m.module_id} style={{ display: "flex",
                      alignItems: "center", gap: 10, fontSize: 11 }}>
                      <div style={{ flex: 1, color: "#a5b8b3" }}>{m.module_title}</div>
                      <div style={{ flex: 1, maxWidth: 240, height: 6,
                        background: "#091514", borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ height: "100%",
                          width: `${m.percent_complete}%`,
                          background: m.percent_complete >= 80 ? "#3dc0af"
                            : (m.percent_complete > 0 ? "#f0be3c" : "transparent") }} />
                      </div>
                      <div style={{ width: 80, textAlign: "right", color: "#7b8b87" }}>
                        {m.lessons_completed}/{m.lessons_total} · {m.percent_complete}%
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </ProfileSection>

          {/* 3. Exam attempts */}
          <ProfileSection title={`Exam attempts (${profile.exam_attempts.length})`}>
            {profile.exam_attempts.length === 0 ? (
              <div style={{ color: "#7b8b87", fontSize: 11 }}>No exam attempts yet.</div>
            ) : (
              <div style={{ display: "grid", gap: 4 }}>
                {profile.exam_attempts.map((a) => (
                  <div key={a.id} style={{ display: "flex", gap: 12,
                    padding: "7px 10px", background: "#091514", borderRadius: 3,
                    fontSize: 11 }}>
                    <div style={{ width: 150, color: "#7b8b87" }}>{fmtDate(a.started_at)}</div>
                    <div style={{ width: 90, color: "#a5b8b3" }}>{a.status}</div>
                    <div style={{ width: 70,
                      color: a.score_pct != null ? "#e2ece9" : "#7b8b87" }}>
                      {a.score_pct != null ? `${a.score_pct}%` : "—"}
                    </div>
                    <div style={{ color: a.passed === true ? "#3dc0af"
                      : (a.passed === false ? "#f47171" : "#7b8b87") }}>
                      {a.passed === true ? "✓ passed" : (a.passed === false ? "✗ failed" : "—")}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ProfileSection>

          {/* 4. Payments (collapsible-ish, just a smaller section below the fold) */}
          <ProfileSection title={`Payment history (${profile.payments.length})`}>
            {profile.payments.length === 0 ? (
              <div style={{ color: "#7b8b87", fontSize: 11 }}>No payments yet.</div>
            ) : (
              <div style={{ display: "grid", gap: 4, fontSize: 10 }}>
                {profile.payments.map((p) => (
                  <div key={p.session_id} style={{ padding: "7px 10px",
                    background: "#091514", borderRadius: 3, display: "flex",
                    gap: 12, flexWrap: "wrap" }}>
                    <span style={{ color: "#7b8b87", width: 140 }}>
                      {fmtDate(p.provisioned_at || p.created_at)}
                    </span>
                    <span style={{ color: "#e2ece9", flex: 1 }}>{p.course_id || "—"}</span>
                    <span style={{ color: "#a5b8b3" }}>
                      {p.amount_total != null && p.currency
                        ? `${(p.amount_total / 100).toFixed(2)} ${p.currency.toUpperCase()}`
                        : "—"}
                    </span>
                    <span style={{ color: p.payment_status === "paid" ? "#3dc0af" : "#f0be3c" }}>
                      {p.payment_status || "—"}
                    </span>
                    {p.simulated && <span style={{ color: "#c9a8ff" }}>simulated</span>}
                    {p.invoice_number && <span style={{ color: "#7b8b87" }}>{p.invoice_number}</span>}
                  </div>
                ))}
              </div>
            )}
          </ProfileSection>

          {/* 5. Welcome-email audit (debugging) */}
          <ProfileSection title={`Welcome-email audit (${profile.email_audit.length})`}>
            {profile.email_audit.length === 0 ? (
              <div style={{ color: "#7b8b87", fontSize: 11 }}>No welcome-email events logged.</div>
            ) : (
              <div style={{ display: "grid", gap: 4, fontSize: 10 }}>
                {profile.email_audit.map((e, i) => (
                  <div key={i} style={{ padding: "7px 10px",
                    background: "#091514", borderRadius: 3, display: "flex",
                    gap: 12, flexWrap: "wrap" }}>
                    <span style={{ color: "#7b8b87", width: 140 }}>{fmtDate(e.timestamp)}</span>
                    <span style={{ color: "#c9a8ff", width: 110 }}>{e.kind}</span>
                    <span style={{ color: e.status === "sent" ? "#3dc0af"
                      : (e.status === "failed" ? "#f47171" : "#f0be3c") }}>
                      {e.status}
                    </span>
                    {e.status_code && <span style={{ color: "#7b8b87" }}>HTTP {e.status_code}</span>}
                    {e.message_id && <span style={{ color: "#7b8b87" }}>{e.message_id.slice(0, 12)}…</span>}
                    {e.error && <span style={{ color: "#f47171", flexBasis: "100%" }}>{clip(e.error, 140)}</span>}
                  </div>
                ))}
              </div>
            )}
          </ProfileSection>
        </div>
      </div>
    </div>
  );
}


/* --------------------------------------------------------------------
   EmailStudentButton — mailto: link that opens the admin's email client
   pre-filled with a smart subject + greeting. Disabled for school
   students (no email on file). Rendered as a <td> cell on the Students
   table and inside the profile drawer header.
   -------------------------------------------------------------------- */
function EmailStudentButton({ email, name, firstName, pct, testId, large = false }) {
  if (!email) {
    return (
      <span
        title="No email on file — contact the school admin"
        data-testid={testId ? `${testId}-disabled` : undefined}
        style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          padding: large ? "6px 12px" : "4px 10px",
          border: "1px dashed #2a403c", borderRadius: 999,
          color: "#4a5a56", fontSize: 10, cursor: "not-allowed",
          letterSpacing: "0.1em", textTransform: "uppercase",
        }}
      >
        ✉ No email
      </span>
    );
  }
  const first = (firstName || (name || "").split(" ")[0] || "there").trim();
  const subject = "Your TGI Ground School progress";
  const body = `Hi ${first},\n\n`;
  const href = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  return (
    <a
      href={href}
      onClick={(e) => e.stopPropagation()}
      data-testid={testId}
      title={`Email ${email}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: large ? "6px 14px" : "4px 10px",
        border: "1px solid rgba(61,192,175,0.4)",
        background: "rgba(61,192,175,0.08)",
        color: "#3dc0af",
        borderRadius: 999, fontSize: 10,
        letterSpacing: "0.1em", textTransform: "uppercase",
        textDecoration: "none", fontWeight: 600, whiteSpace: "nowrap",
      }}
    >
      ✉ Email
    </a>
  );
}

function ProfileSection({ title, children }) {
  return (
    <div>
      <div style={{ font: "700 10px var(--vw-mono)", letterSpacing: "0.2em",
        color: "#7b8b87", textTransform: "uppercase", marginBottom: 10 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function KV({ label, value, color }) {
  return (
    <div style={{ background: "#091514", border: "1px solid #1c2a27",
      borderRadius: 4, padding: "10px 12px" }}>
      <div style={{ font: "700 9px var(--vw-mono)", letterSpacing: "0.2em",
        color: "#7b8b87", textTransform: "uppercase", marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ font: "600 13px 'Montserrat','Geist','Inter'",
        color: color || "#e2ece9" }}>
        {value}
      </div>
    </div>
  );
}


/* KindCard — one tile per email kind in the Email Health by-kind
   breakdown. Shows friendly label + total volume + sent/failed/
   skipped/throttled line so support can tell at a glance where the
   bulk of email volume (or failure) is coming from. */
function KindCard({ bk }) {
  const META = {
    welcome: {
      label: "Onboarding",
      hint: "New-buyer welcome emails",
      accent: "#3dc0af",
    },
    password_reset: {
      label: "Password reset",
      hint: "Student-initiated forgot-password",
      accent: "#c9a8ff",
    },
    welcome_resend: {
      label: "Admin rescue",
      hint: "Admin-triggered resend-welcome",
      accent: "#f0be3c",
    },
  };
  const meta = META[bk.kind] || { label: bk.kind, hint: "Other", accent: "#e2ece9" };
  const anyFail = bk.failed > 0 || bk.skipped > 0;
  return (
    <div style={{
      background: "#091514", border: "1px solid #1c2a27", borderRadius: 4,
      padding: "12px 14px", display: "grid", gap: 8,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div>
          <div style={{ font: "700 11px 'Montserrat','Geist','Inter'",
            color: meta.accent, letterSpacing: "0.03em" }}>
            {meta.label}
          </div>
          <div style={{ font: "500 10px var(--vw-mono)", color: "#7b8b87", marginTop: 2 }}>
            {meta.hint}
          </div>
        </div>
        <div style={{ font: "700 22px 'Montserrat','Geist','Inter'",
          color: "#e2ece9", letterSpacing: "-0.02em" }}>
          {bk.total}
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap",
        font: "500 10px var(--vw-mono)", color: "#a5b8b3" }}>
        <span style={{ color: "#3dc0af" }}>✓ {bk.sent} sent</span>
        {bk.failed > 0 && <span style={{ color: "#f47171" }}>✗ {bk.failed} failed</span>}
        {bk.skipped > 0 && <span style={{ color: "#f0be3c" }}>⚠ {bk.skipped} skipped</span>}
        {bk.throttled > 0 && <span style={{ color: "#c9a8ff" }}>⏳ {bk.throttled} throttled</span>}
        {bk.total === 0 && <span style={{ color: "#7b8b87" }}>no activity in window</span>}
        {!anyFail && bk.total > 0 && bk.throttled === 0 && (
          <span style={{ color: "#7b8b87" }}>· 100% delivered</span>
        )}
      </div>
    </div>
  );
}


/* --------------------------------------------------------------------
   PaymentAnalyticsPanel — revenue + buyer + by-course breakdown over
   the last 7/30/90 days. Backed by GET /api/payments/admin/analytics.
   Mirrors the Email Health tile pattern: headline KPI, stat row, then
   a per-course table.
   -------------------------------------------------------------------- */
function PaymentAnalyticsPanel({ onLogout }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (d) => {
    setLoading(true);
    try {
      const r = await authedFetch(`${Q_API}/payments/admin/analytics?days=${d}`);
      setData(r);
    } catch (e) {
      if (e.message === "AUTH") { onLogout(); return; }
      toast.error("Failed to load analytics");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(days); /* eslint-disable-next-line */ }, [days]);

  if (!data) {
    return <div style={{ color: "#7b8b87", padding: 24, fontFamily: "var(--vw-mono)", fontSize: 12 }}>
      {loading ? "Loading analytics…" : "No data."}
    </div>;
  }

  const money = (cents) => `${(cents / 100).toFixed(2)} ${data.currency.toUpperCase()}`;
  const empty = data.units === 0 && data.simulated_units === 0;

  return (
    <div data-testid="admin-analytics-panel" style={{ display: "grid", gap: 16 }}>
      {/* Range toggle */}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        {[7, 30, 90].map((d) => (
          <button key={d}
            onClick={() => setDays(d)}
            data-testid={`admin-analytics-range-${d}`}
            style={{
              background: days === d ? "#3dc0af" : "transparent",
              color: days === d ? "#051210" : "#a5b8b3",
              border: "1px solid " + (days === d ? "#3dc0af" : "#2a403c"),
              padding: "6px 14px", borderRadius: 999,
              font: "700 10px var(--vw-mono)", letterSpacing: "0.15em",
              textTransform: "uppercase", cursor: "pointer",
            }}>
            {d}d
          </button>
        ))}
      </div>

      {/* Headline */}
      <div style={{
        background: "#091514", border: "1px solid #1c2a27", borderRadius: 4,
        padding: "16px 18px",
      }}>
        <div style={{ font: "700 10px var(--vw-mono)", letterSpacing: "0.2em",
          color: "#7b8b87", textTransform: "uppercase", marginBottom: 6 }}>
          Revenue · last {data.window_days} days
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
          <div data-testid="admin-analytics-revenue"
            style={{ font: "700 36px 'Montserrat','Geist','Inter'",
              color: data.units > 0 ? "#3dc0af" : "#7b8b87",
              letterSpacing: "-0.02em" }}>
            {money(data.revenue_cents)}
          </div>
          <div style={{ color: "#7b8b87", font: "500 12px var(--vw-mono)" }}>
            {data.units} successful purchase{data.units === 1 ? "" : "s"} · {data.unique_buyers} unique buyer{data.unique_buyers === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <AnalyticsStat label="Units" value={data.units} color={data.units > 0 ? "#3dc0af" : "#e2ece9"} />
        <AnalyticsStat label="Buyers" value={data.unique_buyers} color={data.unique_buyers > 0 ? "#3dc0af" : "#e2ece9"} />
        <AnalyticsStat label="Failed checkouts" value={data.failed_checkouts}
          color={data.failed_checkouts > 0 ? "#f0be3c" : "#e2ece9"} />
        <AnalyticsStat label="Simulated (excluded)" value={data.simulated_units}
          color={data.simulated_units > 0 ? "#c9a8ff" : "#e2ece9"} />
      </div>

      {/* Per-course breakdown */}
      <div>
        <div style={{ font: "700 10px var(--vw-mono)", letterSpacing: "0.2em",
          color: "#7b8b87", textTransform: "uppercase", margin: "4px 0 10px" }}>
          Revenue by course
        </div>
        {empty ? (
          <div data-testid="admin-analytics-empty"
            style={{ color: "#7b8b87", padding: "14px 16px",
              background: "#091514", border: "1px solid #1c2a27",
              borderRadius: 4, font: "500 12px var(--vw-mono)" }}>
            No purchases in the last {data.window_days} days.
          </div>
        ) : data.by_course.length === 0 ? (
          <div style={{ color: "#7b8b87", padding: "14px 16px", background: "#091514",
            border: "1px solid #1c2a27", borderRadius: 4,
            font: "500 12px var(--vw-mono)" }}>
            {data.simulated_units} simulated test transaction{data.simulated_units === 1 ? "" : "s"} — no real revenue in window.
          </div>
        ) : (
          <div data-testid="admin-analytics-by-course"
            style={{ overflowX: "auto", border: "1px solid #1c2a27", borderRadius: 4 }}>
            <table style={{ width: "100%", borderCollapse: "collapse",
              fontFamily: "var(--vw-mono)", fontSize: 11, color: "#e2ece9" }}>
              <thead>
                <tr>
                  {["Course", "Units", "Revenue", "Avg sale"].map((h) => (
                    <th key={h} style={{ background: "#0d1817", color: "#7b8b87",
                      textAlign: "left", padding: "9px 12px",
                      borderBottom: "1px solid #1c2a27", letterSpacing: "0.2em",
                      textTransform: "uppercase", fontSize: 10 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.by_course.map((c, i) => (
                  <tr key={c.course_id}
                    style={{ borderBottom: "1px solid #11201d",
                      background: i % 2 ? "#070f0e" : "#091514" }}>
                    <td style={{ padding: "8px 12px", color: "#e2ece9" }}>{c.course_title}</td>
                    <td style={{ padding: "8px 12px" }}>{c.units}</td>
                    <td style={{ padding: "8px 12px", color: "#3dc0af" }}>{money(c.revenue_cents)}</td>
                    <td style={{ padding: "8px 12px", color: "#7b8b87" }}>
                      {c.units > 0 ? money(Math.round(c.revenue_cents / c.units)) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function AnalyticsStat({ label, value, color }) {
  return (
    <div style={{
      flex: 1, minWidth: 140,
      background: "#091514", border: "1px solid #1c2a27", borderRadius: 4,
      padding: "14px 16px",
    }}>
      <div style={{ font: "700 10px var(--vw-mono)", letterSpacing: "0.2em",
        color: "#7b8b87", textTransform: "uppercase", marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ font: "700 22px 'Montserrat','Geist','Inter'", color: color || "#e2ece9" }}>
        {value}
      </div>
    </div>
  );
}


/* Shared pill-dropdown style for the Students-tab filters. */
const _filterStyle = {
  background: "#091514", border: "1px solid #2a403c",
  borderRadius: 999, color: "#a5b8b3", padding: "9px 14px",
  fontFamily: "var(--vw-mono)", fontSize: 11, letterSpacing: "0.05em",
  cursor: "pointer",
};

